import {Injectable, inject} from '@angular/core'
import {TranslateService} from '@ngx-translate/core'
import {lastValueFrom} from 'rxjs'
import {EmbeddedClientService} from '@service/embeddingService/embedded-client.service'
import {PrimeNGSetupService} from '../primeng-setup.service'

/**
 * Servicio de inicialización global de la aplicación.
 *
 * @description
 * Orquesta el arranque de la aplicación en paralelo: inicializa el sistema de traducciones
 * (ngx-translate), la integración con el host cuando la app está embebida (microfrontend) y
 * la configuración de PrimeNG. Es invocado una sola vez al iniciar la aplicación, antes de
 * renderizar el primer componente, para garantizar que las dependencias globales estén listas.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * ```typescript
 * const initService = inject(InitService)
 * await initService.initApp()
 * ```
 *
 * ### Best Practices
 *
 * - Invocar `initApp()` una única vez durante el bootstrap de la aplicación.
 * - Ejecutar antes de montar rutas o navegaciones para evitar textos sin traducir.
 * - Los tres procesos se lanzan en paralelo con `Promise.all`; si alguno falla, la promesa rechaza.
 *
 * ### Bad Uses / Errores comunes
 *
 * - No invocar `initApp()` múltiples veces: re-ejecutar la inicialización puede duplicar
 *   efectos (handshake peer-to-peer, registro en el host embebido, etc.).
 * - No llamarlo desde componentes hijos: su lugar natural es el bootstrap.
 *
 * @example
 * ```typescript
 * // main.ts (fragmento)
 * const initService = appRef.injector.get(InitService)
 * await initService.initApp()
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class InitService {
  private readonly translateService = inject(TranslateService)
  private readonly embeddingClientService = inject(EmbeddedClientService)
  private readonly primeNGSetupService = inject(PrimeNGSetupService)

  /**
   * Inicializa en paralelo los servicios globales requeridos antes de arrancar la UI.
   *
   * @description
   * Lanza simultáneamente la inicialización del cliente embebido, las traducciones y
   * la configuración de PrimeNG. Resuelve cuando todos terminan; rechaza si alguno falla.
   *
   * @returns Promesa que resuelve cuando todas las inicializaciones han completado.
   */
  initApp = async (): Promise<void> => {
    await Promise.all([
      this.initEmmbeddingService(),
      this.initTranslateService(),
      this.primeNGSetupService.initializePrimeNG(),
    ])
  }

  /**
   * Configura el idioma por defecto y carga el idioma del navegador en ngx-translate.
   *
   * @description
   * Establece `es` como idioma por defecto y aplica el idioma reportado por el navegador
   * mediante `translateService.use()`, convirtiendo el observable a promesa.
   *
   * @returns Promesa con el objeto de traducciones cargadas.
   */
  initTranslateService = async (): Promise<any> => {
    this.translateService.setFallbackLang('es')
    return lastValueFrom(this.translateService.use(this.translateService.getBrowserLang()))
  }

  /**
   * Registra la app en el host cuando se ejecuta como microfrontend embebido.
   *
   * @description
   * Si la aplicación corre embebida (detectado por `isEmbeddedFunction()`), se registra
   * contra el padre con la clave `'street'` y espera el payload inicial. En modo standalone
   * devuelve `true` inmediatamente sin hacer nada.
   *
   * @returns `true` cuando la app no está embebida o cuando el payload ha sido recibido.
   */
  private readonly initEmmbeddingService = async (): Promise<boolean> => {
    if (this.embeddingClientService.isEmbeddedFunction()) {
      this.embeddingClientService.registerToParent('street')
      return await this.embeddingClientService.waitPeerPayload()
    }
    return true
  }
}
